import React from 'react';
import Link from 'next/link';

export function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export function formatINR(n: number) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

/** Format a SaaS/billing amount in the shop's currency (INR or USD). */
export function formatMoney(n: number, currency: string = 'INR') {
  const amount = Number(n || 0);
  if (currency === 'USD') {
    return `$${amount.toLocaleString('en-US')}`;
  }
  return formatINR(amount);
}

const PAGE_WIDTH = {
  wide: 'max-w-[1440px]',
  default: 'max-w-[1200px]',
  narrow: 'max-w-[1100px]',
  form: 'max-w-[900px]',
} as const;

export function PageFrame({
  children,
  width = 'wide',
  className,
}: {
  children: React.ReactNode;
  width?: keyof typeof PAGE_WIDTH;
  className?: string;
}) {
  return <div className={cn('page-frame mx-auto w-full', PAGE_WIDTH[width], className)}>{children}</div>;
}

/** Full-width detail pages with tighter canvas padding than list views. */
export function DetailPageFrame({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('page-frame-detail mx-auto w-full max-w-[1600px]', className)}>{children}</div>;
}

export function SectionLabel({
  children,
  count,
}: {
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-500">{children}</h2>
      {typeof count === 'number' && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-ink-900 px-1.5 text-[11px] font-bold text-white num">
          {count}
        </span>
      )}
    </div>
  );
}

export function Toolbar({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-end gap-2 rounded-[10px] border border-ink-200 bg-white p-3',
        className
      )}
    >
      {children}
    </div>
  );
}

export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block min-w-[140px] flex-1', className)}>
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-400">{label}</span>
      {children}
    </label>
  );
}

export function CardHeader({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-ink-900">{title}</div>
        {hint && <p className="mt-0.5 text-[12px] text-ink-500">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-success-soft text-success',
    trial: 'bg-brand-50 text-brand-700',
    pending: 'bg-warning-soft text-warning',
    draft: 'bg-ink-100 text-ink-600',
    unused: 'bg-ink-100 text-ink-600',
    used: 'bg-brand-50 text-brand-700',
    activated: 'bg-brand-50 text-brand-700',
    completed: 'bg-success-soft text-success',
    suspended: 'bg-danger-soft text-danger',
    rejected: 'bg-danger-soft text-danger',
    cancelled: 'bg-ink-100 text-ink-600',
    expired: 'bg-ink-100 text-ink-600',
    due: 'bg-warning-soft text-warning',
    paid: 'bg-success-soft text-success',
    processing: 'bg-brand-50 text-brand-700',
    terminated: 'bg-ink-200 text-ink-700',
    failed: 'bg-danger-soft text-danger',
    queued: 'bg-warning-soft text-warning',
    pushed: 'bg-brand-50 text-brand-700',
    reached: 'bg-warning-soft text-warning',
    applied: 'bg-success-soft text-success',
  };
  const labels: Record<string, string> = {
    queued: 'Waiting',
    pushed: 'Pushed',
    reached: 'Reached',
    applied: 'Working',
    failed: 'Failed',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
        map[status] ?? 'bg-ink-100 text-ink-600'
      )}
    >
      {labels[status] ?? status}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-[22px] font-bold tracking-tight text-ink-950">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Standard detail-page header: back-link row + title/status/actions. */
export function DetailHeader({
  backHref,
  backLabel,
  extraLinks,
  title,
  subtitle,
  status,
  actions,
}: {
  backHref: string;
  backLabel: string;
  extraLinks?: { href: string; label: string }[];
  title: string;
  subtitle?: string;
  status?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[12px]">
        <Link href={backHref} className="font-semibold text-brand-600 hover:underline">
          ← {backLabel}
        </Link>
        {extraLinks?.map(l => (
          <React.Fragment key={l.href}>
            <span className="text-ink-300">·</span>
            <Link href={l.href} className="font-semibold text-brand-600 hover:underline">
              {l.label}
            </Link>
          </React.Fragment>
        ))}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[22px] font-bold tracking-tight text-ink-950">{title}</h1>
            {status && <StatusBadge status={status} />}
          </div>
          {subtitle && <p className="mt-1 text-[13px] text-ink-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-[10px] border border-ink-200 bg-white p-4">
      <div className="text-[12px] font-semibold text-ink-500">{label}</div>
      <div className="mt-1 text-[20px] font-bold tracking-tight text-ink-950 num">{value}</div>
      {sub && <div className="mt-0.5 text-[12px] text-ink-500">{sub}</div>}
    </div>
  );
}

/** Metric-aware status for Command Center cards. */
export type MetricTone = 'critical' | 'warning' | 'healthy' | 'neutral';

const METRIC_TONE_STYLES: Record<
  MetricTone,
  { wrap: string; value: string; action: string; pill: string }
> = {
  critical: {
    wrap: 'border-ink-200 bg-white',
    value: 'text-ink-950',
    action: 'text-brand-700',
    pill: 'bg-status-critical-soft text-status-critical',
  },
  warning: {
    wrap: 'border-ink-200 bg-white',
    value: 'text-ink-950',
    action: 'text-brand-700',
    pill: 'bg-status-warning-soft text-status-warning',
  },
  healthy: {
    wrap: 'border-ink-200 bg-white',
    value: 'text-ink-950',
    action: 'text-brand-700',
    pill: 'bg-brand-50 text-brand-800',
  },
  neutral: {
    wrap: 'border-ink-200 bg-white',
    value: 'text-ink-950',
    action: 'text-brand-700',
    pill: '',
  },
};

/**
 * Higher-is-worse rate → tone. Zero reads as healthy-clear, not empty.
 * warningAt / criticalAt are inclusive lower bounds for those tones.
 */
export function toneForWorseRate(
  rate: number,
  warningAt = 1,
  criticalAt = 10
): MetricTone {
  if (rate <= 0) return 'healthy';
  if (rate >= criticalAt) return 'critical';
  if (rate >= warningAt) return 'warning';
  return 'healthy';
}

/** Higher count = worse (e.g. uninstalled devices, pending items). */
export function toneForWorseCount(count: number, warningAt = 1, criticalAt = 25): MetricTone {
  if (count <= 0) return 'healthy';
  if (count >= criticalAt) return 'critical';
  if (count >= warningAt) return 'warning';
  return 'healthy';
}

export function MetricCard({
  label,
  value,
  sub,
  href,
  tone = 'neutral',
  actionLabel = 'Review',
  showHealthyCheck = false,
}: {
  label: string;
  value: string;
  sub?: string;
  href?: string;
  tone?: MetricTone;
  actionLabel?: string;
  /** When tone is healthy and value is a clear zero, show a check affordance. */
  showHealthyCheck?: boolean;
}) {
  const styles = METRIC_TONE_STYLES[tone];
  const pillLabel = tone === 'critical' ? 'Critical' : tone === 'warning' ? 'Watch' : tone === 'healthy' && showHealthyCheck ? 'OK' : null;
  const body = (
    <div
      className={cn(
        'rounded-[10px] border p-4 transition duration-200',
        styles.wrap,
        href && 'hover:border-brand-600/40 hover:bg-brand-50/40'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12px] font-semibold text-ink-500">{label}</div>
        {pillLabel && (
          <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide', styles.pill)}>
            {pillLabel}
          </span>
        )}
      </div>
      <div className={cn('mt-2 text-[22px] font-bold tracking-tight num', styles.value)}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[12px] leading-snug text-ink-500">{sub}</div>}
      {href && (
        <div className={cn('mt-3 text-[12px] font-semibold', styles.action)}>
          {actionLabel}
        </div>
      )}
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="block group">
        {body}
      </Link>
    );
  }
  return body;
}

export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-[10px] border border-ink-200 bg-white', className)}>
      {children}
    </div>
  );
}

export function Btn({
  children,
  variant = 'primary',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}) {
  const styles = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700',
    secondary: 'bg-white text-ink-800 border border-ink-200 hover:bg-ink-50',
    danger: 'bg-danger text-white hover:bg-red-600',
    ghost: 'text-ink-600 hover:bg-ink-100',
  };
  return (
    <button
      type="button"
      className={cn(
        'inline-flex min-h-9 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold transition duration-150 disabled:cursor-not-allowed disabled:opacity-50',
        styles[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-[13px] text-ink-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
        props.className
      )}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-[13px] text-ink-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
        props.className
      )}
    />
  );
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-ink-950/50 backdrop-blur-[2px]"
        onClick={confirmDisabled ? undefined : onCancel}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-ink-200 bg-white p-5 shadow-[var(--shadow-pop)]">
        <h3 className="text-[16px] font-extrabold text-ink-950">{title}</h3>
        <p className="mt-2 text-[13px] text-ink-600">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onCancel} disabled={confirmDisabled}>
            Cancel
          </Btn>
          <Btn
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export function TempPasswordModal({
  open,
  name,
  password,
  onClose,
  emailed,
}: {
  open: boolean;
  name: string;
  password: string;
  onClose: () => void;
  emailed?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-ink-950/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-ink-200 bg-white p-5 shadow-[var(--shadow-pop)]">
        <div className="mb-1 text-[16px] font-extrabold text-ink-950">Account created</div>
        <p className="mb-4 text-[13px] text-ink-600">
          {emailed
            ? <>Login details were emailed to <span className="font-bold">{name}</span>. A copy is shown below in case you need it.</>
            : <>Could not email <span className="font-bold">{name}</span>. Share this one-time password now — it will not be shown again.</>}
        </p>
        <div className="flex items-center gap-2 rounded-lg border border-ink-200 bg-ink-50 px-4 py-3">
          <span className="flex-1 font-mono text-[15px] font-bold tracking-widest text-ink-900">
            {password}
          </span>
          <Btn
            variant="secondary"
            onClick={() => navigator.clipboard.writeText(password)}
          >
            Copy
          </Btn>
        </div>
        <div className="mt-4 flex justify-end">
          <Btn onClick={onClose}>Done</Btn>
        </div>
      </div>
    </div>
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-[13px] text-ink-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
        props.className
      )}
    />
  );
}

export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = React.useState('');
  if (!open) return null;
  const valid = reason.trim().length >= 10;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-ink-950/50 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-2xl border border-ink-200 bg-white p-5 shadow-[var(--shadow-pop)]">
        <h3 className="text-[16px] font-extrabold text-ink-950">{title}</h3>
        <p className="mt-2 text-[13px] text-ink-600">{body}</p>
        <label className="mt-4 block text-[11px] font-bold uppercase text-ink-400">
          Reason (required)
        </label>
        <Textarea
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Explain why this action is being taken…"
          className="mt-1"
        />
        <p className="mt-1 text-[11px] text-ink-400">Minimum 10 characters. Logged in audit trail.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onCancel}>
            Cancel
          </Btn>
          <Btn
            variant={danger ? 'danger' : 'primary'}
            disabled={!valid}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  compact,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Tighter padding for embedded table/card empty rows */
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact
          ? 'px-2 py-5'
          : 'rounded-xl border border-dashed border-ink-300 bg-white/60 px-6 py-12'
      )}
    >
      <div className="text-[15px] font-bold text-ink-800">{title}</div>
      {description && <p className="mt-1 max-w-sm text-[13px] text-ink-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="animate-pulse space-y-2 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="h-8 flex-1 rounded bg-ink-100" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Generic pulse block for building skeletons that mirror a page's real layout. */
export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-xl bg-ink-100', className)} />;
}

/** Loading placeholder for `[id]` detail pages — back-link + title/status + N card slots. */
export function DetailPageSkeleton({ cardCount = 2 }: { cardCount?: number }) {
  return (
    <div>
      <SkeletonBlock className="mb-3 h-3 w-28" />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <SkeletonBlock className="h-7 w-64 max-w-full" />
          <SkeletonBlock className="mt-2 h-3 w-44" />
        </div>
        <SkeletonBlock className="h-6 w-20 shrink-0 rounded-full" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: cardCount }).map((_, i) => (
          <SkeletonBlock key={i} className="h-44" />
        ))}
      </div>
    </div>
  );
}

/** Dialog that collects a required text field (payment ref, notes, etc.). */
export function PromptDialog({
  open,
  title,
  body,
  label,
  placeholder,
  minLength = 3,
  confirmLabel = 'Confirm',
  danger,
  error,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  label: string;
  placeholder?: string;
  minLength?: number;
  confirmLabel?: string;
  danger?: boolean;
  error?: string | null;
  loading?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = React.useState('');
  React.useEffect(() => {
    if (open) setValue('');
  }, [open]);
  if (!open) return null;
  const valid = value.trim().length >= minLength;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-ink-950/50 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-2xl border border-ink-200 bg-white p-5 shadow-[var(--shadow-pop)]">
        <h3 className="text-[16px] font-extrabold text-ink-950">{title}</h3>
        <p className="mt-2 text-[13px] text-ink-600">{body}</p>
        <label className="mt-4 block text-[11px] font-bold uppercase text-ink-400">{label}</label>
        <Textarea
          rows={3}
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={placeholder}
          className="mt-1"
          autoFocus
        />
        <p className="mt-1 text-[11px] text-ink-400">Minimum {minLength} characters.</p>
        {error && <p className="mt-2 text-[12px] font-semibold text-danger">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Btn>
          <Btn
            variant={danger ? 'danger' : 'primary'}
            disabled={!valid || loading}
            onClick={() => onConfirm(value.trim())}
          >
            {loading ? 'Saving…' : confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

type ToastItem = { id: number; message: string; tone: 'success' | 'error' | 'info' };

const ToastCtx = React.createContext<{
  toast: (message: string, tone?: ToastItem['tone']) => void;
} | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const toast = React.useCallback((message: string, tone: ToastItem['tone'] = 'success') => {
    const id = Date.now() + Math.random();
    setItems(prev => [...prev, { id, message, tone }]);
    setTimeout(() => setItems(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2">
        {items.map(t => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto rounded-xl border px-4 py-3 text-[13px] font-semibold shadow-lg',
              t.tone === 'success' && 'border-success/30 bg-success-soft text-success',
              t.tone === 'error' && 'border-danger/30 bg-danger-soft text-danger',
              t.tone === 'info' && 'border-brand-200 bg-brand-50 text-brand-800'
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(ToastCtx);
  if (!ctx) {
    return {
      toast: (message: string) => {
        if (typeof window !== 'undefined') console.info('[toast]', message);
      },
    };
  }
  return ctx;
}
